import { action } from "typesafe-actions";
import { IDisposition } from "./disposition.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import { closeLoaderWithMessage, openLoaderWithMessage, showMessage } from "../messages/messagesActions";
import { endPoints } from "../../constants/endPoints";
import { ISearchQueryParams } from "../common/common.types";
import { getSearchQueryV2 } from "../common/helpers";

// Fetch Disposition List Actions
export const FETCH_DISPOSITION_LIST_PROGRESS = "FETCH_DISPOSITION_LIST_PROGRESS";
export const FETCH_DISPOSITION_LIST_SUCCESS = "FETCH_DISPOSITION_LIST_SUCCESS";
export const FETCH_DISPOSITION_LIST_FAILED = "FETCH_DISPOSITION_LIST_FAILED";

export const fetchDispositionListProgress = () => action(FETCH_DISPOSITION_LIST_PROGRESS);
export const fetchDispositionListSuccess = (list: IDisposition[], totalRecords: number) =>
  action(FETCH_DISPOSITION_LIST_SUCCESS, { list, totalRecords });
export const fetchDispositionListFailed = () => action(FETCH_DISPOSITION_LIST_FAILED);


export const fetchDispositionListAsync =
  (queryParams: ISearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchDispositionListProgress());

        const {
          page,
          rowsPerPage,
          status,
          date,
          advancedFilters,
          disposition: dispositionSearch,
          sub_disposition: subDispositionSearch,
          companyOutletUuid
        } = queryParams as any;

        const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];

        if (dispositionSearch) {
          advanceFilter.push({
            column: ["disposition"],
            logicalOperator: "AND",
            operator: "EQUAL",
            value: dispositionSearch,
          });
        }

        if (subDispositionSearch) {
          advanceFilter.push({
            column: ["disposition"],
            logicalOperator: "AND",
            operator: "EQUAL",
            value: subDispositionSearch,
          });
        }

        let finalUrl = `${endPoints.disposition.getDisposition}?pageNo=${page}&itemPerPage=${rowsPerPage}`;

        if (date?.fromDate && date?.toDate) {
          finalUrl += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
        }

        if (status && status !== "-1") {
          finalUrl += `&status=${status}`;
        }

        if (companyOutletUuid) {
          finalUrl += `&company_outlet_uuid=${companyOutletUuid}`;
        }

        if (advanceFilter.length > 0) {
          finalUrl += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
        }

        const res = await api.get(finalUrl);
        const data: IDisposition[] = res.data.data || [];
        const totalRecords = res.data.totalRecords || 0;

        dispatch(fetchDispositionListSuccess(data, totalRecords));
      } catch (err: any) {
        dispatch(fetchDispositionListFailed());
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch dispositions",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

// Fetch Single Disposition Actions
export const FETCH_DISPOSITION_PROGRESS = "FETCH_DISPOSITION_PROGRESS";
export const FETCH_DISPOSITION_SUCCESS = "FETCH_DISPOSITION_SUCCESS";
export const FETCH_DISPOSITION_FAILED = "FETCH_DISPOSITION_FAILED";

export const fetchDispositionProgress = () => action(FETCH_DISPOSITION_PROGRESS);
export const fetchDispositionSuccess = (disposition: IDisposition) =>
  action(FETCH_DISPOSITION_SUCCESS, { disposition });
export const fetchDispositionFailed = (errorMessage: string) =>
  action(FETCH_DISPOSITION_FAILED, { errorMessage });

export const fetchDispositionAsync =
  (dispositionId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchDispositionProgress());

        const res = await api.get(`${endPoints.disposition.getDisposition}?disposition_uuid=${dispositionId}`);
        const data: IDisposition = res.data.data[0];

        dispatch(fetchDispositionSuccess(data));
      } catch (err: any) {
        dispatch(
          fetchDispositionFailed(
            err?.response?.data?.message || "Failed to fetch disposition"
          )
        );
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to fetch disposition",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

// Upsert Disposition Actions
export const upsertDispositionAsync =
  (
    dispositionData: IDisposition,
    onCallBack: (isSuccess?: boolean, data?: IDisposition) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(openLoaderWithMessage("Saving disposition, please wait..."));
        const {
          create_ts,
          insert_ts,
          created_by_name,
          created_by_uuid,
          modified_by_name,
          modified_by_uuid,
          ...payload
        } = dispositionData;

        const res = await api.post(endPoints.disposition.upsertDisposition, payload);

        const data: IDisposition = res.data.data;
        dispatch(
          showMessage({
            message: res.data.message || "Disposition saved successfully",
            type: "success",
            displayAs: "snackbar",
          })
        );
        onCallBack(true, data);
      } catch (err: any) {
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to save disposition",
            type: "error",
            displayAs: "snackbar",
          })
        );
        onCallBack(false);
      } finally {
        dispatch(closeLoaderWithMessage());
      }
    };

// Clear Actions
export const CLEAR_DISPOSITION_STATE = "CLEAR_DISPOSITION_STATE";
export const CLEAR_DISPOSITION = "CLEAR_DISPOSITION";

export const clearDispositionState = () => action(CLEAR_DISPOSITION_STATE);
export const clearDisposition = () => action(CLEAR_DISPOSITION);
